import { useEffect, useState } from 'react';

import { Checkbox, FormControlLabel, FormGroup, Paper } from '@mui/material';
import { Ban } from 'lucide-react';

import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useLocale } from '@shared/hooks/locale';

// "Blocked advertiser" quick filter for the listings list (#24256).
//
// A lot is "of a blocked advertiser" when its creator OR any of its sellers
// is blocked — the set the lots trigger and the auto-reviewer enforce on.
// `sellers` is an array relationship, so Hasura turns a predicate on it into
// an EXISTS; "Yes" is that `_or`, and "No" must be its NEGATION, `_not`.
// The generic null-check filter would write `EXISTS (seller with
// fsbo_blocked_at IS NULL)` for "No" instead, which drops every lot without
// a seller (all agency listings) and matches a two-seller lot on both
// sides. Both clauses go into the root `_and`, written and removed
// structurally, by value, so they coexist with whatever else lives there.
// Never the root `_not`: the Agent / Seller quick filters (UserFilter) write
// their "(none)" choice to it, and two owners of one slot either overwrite
// each other or merge into `_not: { blocked, broker }`, which Hasura reads
// as NOT(blocked AND has-broker) — letting blocked FSBO lots back in.
const BLOCKED_PREDICATE = {
  _or: [
    { sellers: { seller: { fsbo_blocked_at: { _is_null: false } } } },
    { created_by_user: { fsbo_blocked_at: { _is_null: false } } },
  ],
};
const NOT_BLOCKED_PREDICATE = { _not: BLOCKED_PREDICATE };
const YES_KEY = JSON.stringify(BLOCKED_PREDICATE);
const NO_KEY = JSON.stringify(NOT_BLOCKED_PREDICATE);

type Where = Record<string, unknown> & {
  _and?: unknown[];
};

const hasClause = (w: Where, key: string): boolean =>
  Array.isArray(w._and) && w._and.some(c => JSON.stringify(c) === key);
const hasYes = (w: Where): boolean => hasClause(w, YES_KEY);
const hasNo = (w: Where): boolean => hasClause(w, NO_KEY);

// Remove both clauses, whichever is present, without touching the rest.
const strip = (where: Where): Where => {
  const next: Where = JSON.parse(JSON.stringify(where ?? {}));
  if (Array.isArray(next._and)) {
    next._and = next._and.filter(c => {
      const key = JSON.stringify(c);
      return key !== YES_KEY && key !== NO_KEY;
    });
    if (next._and.length === 0) {
      delete next._and;
    }
  }
  return next;
};

export const FsboBlockedAdvertiserFilter = ({
  label,
  where,
  queryParamsScope,
  disabled,
}: QuickFilterProps) => {
  const { t } = useLocale();
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);

  const [yesChecked, setYesChecked] = useState(() => hasYes(where ?? {}));
  const [noChecked, setNoChecked] = useState(() => hasNo(where ?? {}));

  // Sync with URL changes (browser back/forward).
  useEffect(() => {
    setYesChecked(hasYes(where ?? {}));
    setNoChecked(hasNo(where ?? {}));
  }, [where]);

  const apply = (yes: boolean, no: boolean) => {
    const next = strip(where ?? {});
    // Both or neither: no filter.
    const clause =
      yes && !no
        ? BLOCKED_PREDICATE
        : no && !yes
        ? NOT_BLOCKED_PREDICATE
        : null;
    if (clause != null) {
      next._and = [...(Array.isArray(next._and) ? next._and : []), clause];
    }
    setFiltersParams(next);
  };

  const handleDelete = () => {
    setYesChecked(false);
    setNoChecked(false);
    apply(false, false);
  };

  return (
    <FilterChip
      label={label}
      icon={<Ban size={16} />}
      onDelete={yesChecked || noChecked ? handleDelete : undefined}
      disabled={disabled?.(where) ?? false}
      renderFilter={() => (
        <Paper
          elevation={0}
          sx={{
            py: 0.5,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          }}
        >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={yesChecked}
                  onChange={e => {
                    setYesChecked(e.target.checked);
                    apply(e.target.checked, noChecked);
                  }}
                />
              }
              label={t('Yes')}
              sx={{ mx: 0.5 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={noChecked}
                  onChange={e => {
                    setNoChecked(e.target.checked);
                    apply(yesChecked, e.target.checked);
                  }}
                />
              }
              label={t('No')}
              sx={{ mx: 0.5 }}
            />
          </FormGroup>
        </Paper>
      )}
    />
  );
};
